
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

/**
 * Custom hook to prevent external navigation and handle all links within the app
 */
export const useInAppLinks = () => {
  const navigate = useNavigate();
  const isCapacitorApp = Capacitor.isNativePlatform();
  
  useEffect(() => {
    if (!isCapacitorApp) return;
    
    // Function to intercept link clicks and handle them
    const handleLinkClick = async (event: MouseEvent) => {
      // Check if the clicked element is an anchor tag
      const target = event.target as HTMLElement;
      const anchor = target.closest('a');
      
      if (!anchor) return;
      
      const href = anchor.getAttribute('href');
      if (!href) return;
      
      // Skip special links
      if (href.startsWith('#') || 
          href.startsWith('tel:') || 
          href.startsWith('mailto:') || 
          href.startsWith('sms:') ||
          href.startsWith('file:')) return;
      
      // Check if the link is to an external site
      const isExternal = href.startsWith('http') && 
                        !href.includes(window.location.hostname);
      
      // For internal app routes, prevent default and use React Router
      if (!isExternal && href.startsWith('/')) {
        event.preventDefault();
        console.log('Intercepted internal navigation to:', href);
        navigate(href);
        return;
      }
      
      // For external links, open them in the system browser
      if (isExternal) {
        event.preventDefault();
        console.log('External link intercepted:', href);
        
        try {
          // Use Capacitor Browser plugin to open external links
          await Browser.open({ url: href });
        } catch (error) {
          console.error('Error opening external link:', error);
        }
        
        return;
      }
    };
    
    // Add the click event listener to the document
    document.addEventListener('click', handleLinkClick);
    
    // Cleanup function
    return () => {
      document.removeEventListener('click', handleLinkClick);
    };
  }, [navigate, isCapacitorApp]);
};
